import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiInstance from "../apiConfig/apiinstance";

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const token = location.pathname.split("/").filter(Boolean)[2];

  const checkPasswordMatch = (pwd, confirmPwd) => {
    if (confirmPwd && pwd !== confirmPwd) {
      setPasswordMatchError("Passwords do not match");
    } else {
      setPasswordMatchError("");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "password") {
      checkPasswordMatch(value, formData.confirmPassword);
    } else if (name === "confirmPassword") {
      checkPasswordMatch(formData.password, value);
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setTouched({ confirmPassword: true });
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiInstance.post(
        //jai maa kali
        `/api/auth/v1/reset-password/${token}/`,
        {
          new_password: formData.password,
          confirm_new_password: formData.confirmPassword,
        }
      );

      if (response.data.status === 200) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/");
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const EyeIcon = ({ onClick }) => (
    <svg
      onClick={onClick}
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5 cursor-pointer"
      fill="none"
      viewBox="0 0 24 24"
      stroke="black"
      style={{ background: "transparent" }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 
        4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );

  const EyeOffIcon = ({ onClick }) => (
    <svg
      onClick={onClick}
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5 cursor-pointer"
      fill="none"
      viewBox="0 0 24 24"
      stroke="black"
      style={{ background: "transparent" }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 
        0-8.268-2.943-9.542-7a9.964 9.964 0 012.042-3.362M6.223 
        6.223A9.953 9.953 0 0112 5c4.477 0 8.268 
        2.943 9.542 7a9.969 9.969 0 01-4.132 
        5.411M15 12a3 3 0 11-6 0 3 3 0 
        016 0zM3 3l18 18"
      />
    </svg>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-gray-200 p-4 rounded-xl !text-4xl">
        <div>
          <h2 className="mt-6 text-center font-extrabold text-gray-900">
            Reset Your Password
          </h2>
        </div>

        {success ? (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            Password reset successful! Redirecting to login...
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex flex-col gap-3 -space-y-px">
              {/* New Password */}
              <div className="relative">
                <label htmlFor="password" className="sr-only">
                  New Password
                </label>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-lg pr-10"
                  placeholder="New Password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <div className="absolute inset-y-0 right-3 flex items-center">
                  {showPassword ? (
                    <EyeOffIcon onClick={() => setShowPassword(false)} />
                  ) : (
                    <EyeIcon onClick={() => setShowPassword(true)} />
                  )}
                </div>
              </div>

              {/* Confirm Password */}
              <div className="relative">
                <label htmlFor="confirmPassword" className="sr-only">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-lg pr-10"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <div className="absolute inset-y-0 right-3 flex items-center">
                  {showConfirmPassword ? (
                    <EyeOffIcon onClick={() => setShowConfirmPassword(false)} />
                  ) : (
                    <EyeIcon onClick={() => setShowConfirmPassword(true)} />
                  )}
                </div>
                {touched.confirmPassword && passwordMatchError && (
                  <p className="text-red-500 text-sm mt-1">
                    {passwordMatchError}
                  </p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || passwordMatchError}
                className={`group relative w-full flex justify-center py-2 px-4 border border-transparent font-medium rounded-md text-white ${
                  loading
                    ? "bg-indigo-400"
                    : "bg-indigo-600 hover:bg-indigo-700"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
